import React, { type FC, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
} from '@apollo/react-hooks';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import {
	ModalTitle,
	ModalHeader,
	ModalBody,
	ModalFooter,
	CloseButton,
} from '@atlaskit/modal-dialog';
import Button from '@atlaskit/button/new';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

import { SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE } from '../../controller/Controller.tsx';
import {
	GeneralAccess,
	type GeneralAccessProps,
} from '../shareAndRestrictDialog/header/generalAccess/GeneralAccess.tsx';
import type { ErrorFlagReason, SuccessFlagReason } from '../commonTypes.tsx';

import { ClearRestrictionsForFreeMutation } from './mutations/ClearRestrictionsForFreeMutation.graphql.ts';
import type {
	ClearRestrictionsForFreeMutation as ClearRestrictionsForFreeMutationType,
	ClearRestrictionsForFreeMutationVariables,
} from './mutations/__types__/ClearRestrictionsForFreeMutation.ts';

const i18n = defineMessages({
	title: {
		id: 'share-and-restrict-dialog.openAccessWarningDialog.title',
		defaultMessage: 'Open up access to everyone',
		description:
			'Title for the open access warning dialog which appears when the user tries to open up access to everyone on a Free edition site',
	},
	body: {
		id: 'share-and-restrict-dialog.openAccessWarningDialog.body',
		defaultMessage:
			'<b>This can’t be undone on the Free plan.</b> This will restore the content’s access level to the following default level:',
		description:
			'Body text for the open access warning dialog which appears when the user tries to open up access to everyone on a Free edition site',
	},
	cancelButton: {
		id: 'share-and-restrict-dialog.openAccessWarningDialog.cancelButton',
		defaultMessage: 'Cancel',
		description: 'Cancel button text for the open access warning dialog',
	},
	openAccessModalTitle: {
		id: 'share-and-restrict-dialog.openAccessWarningDialog.openAccessModalTitle',
		defaultMessage: 'Open up access to everyone',
		description:
			'Title for the open access warning dialog which appears when the user tries to open up access to everyone on a Free edition site',
	},
	openAccessButton: {
		id: 'share-and-restrict-dialog.openAccessWarningDialog.openAccessButton',
		defaultMessage: 'Open up access',
		description: 'Confirmation button text for the Open access warning dialog',
	},
});

export type OpenAccessWarningDialogProps = {
	generalAccess: GeneralAccessProps;
	closeOpenAccessWarningDialog: () => void;
	contentId: string;
	successFlag: (reason: SuccessFlagReason) => void;
	errorFlag: (reason: ErrorFlagReason) => void;
	closeShareAndRestricDialog: () => void;
};

export const OpenAccessWarningDialog: FC<OpenAccessWarningDialogProps> = ({
	generalAccess,
	closeOpenAccessWarningDialog,
	contentId,
	successFlag,
	errorFlag,
	closeShareAndRestricDialog,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { Modal } = useChoreographerAssets();

	const [isLoading, setIsLoading] = useState(false);

	const [clearRestrictionsForFreeMutation] = useMutation<
		ClearRestrictionsForFreeMutationType,
		ClearRestrictionsForFreeMutationVariables
	>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		ClearRestrictionsForFreeMutation,
		{
			onCompleted: () => {
				setIsLoading(false);
				successFlag('access-updated');
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
						action: 'success',
						actionSubject: 'openAccessForFree',
					},
				}).fire();
				closeOpenAccessWarningDialog();
				closeShareAndRestricDialog();
			},

			onError: (error) => {
				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
						action: 'errored',
						actionSubject: 'openAccessForFree',
						attributes: {
							message: error?.message,
							name: error?.name,
							stack: error?.stack,
							cause: error?.cause,
						},
					},
				}).fire();
				setIsLoading(false);
				errorFlag('mutation-error');
			},
		},
	);

	const onOpenAccess = () => {
		setIsLoading(true);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId: 'openAccessForFree',
			},
		}).fire();

		void clearRestrictionsForFreeMutation({
			variables: {
				contentId,
			},
		});
	};

	return (
		<Modal width="small" testId="modal-dialog-open-access-warning">
			<ModalHeader>
				<ModalTitle appearance="warning">
					<FormattedMessage {...i18n.openAccessModalTitle} />
				</ModalTitle>
				<CloseButton onClick={closeOpenAccessWarningDialog} />
			</ModalHeader>
			<ModalBody>
				<Box paddingBlockEnd="space.200">
					<FormattedMessage
						{...i18n.body}
						values={{ b: (chunks: React.ReactNode[]) => <Text weight="bold">{chunks}</Text> }}
					/>
				</Box>
				<GeneralAccess {...generalAccess} showBottomPadding />
			</ModalBody>
			<ModalFooter>
				<Inline alignBlock="center" alignInline="end">
					<Inline alignBlock="center" alignInline="center" space="space.100">
						<Button
							appearance="default"
							onClick={closeOpenAccessWarningDialog}
							isDisabled={isLoading}
						>
							<FormattedMessage {...i18n.cancelButton} />
						</Button>
						<Button appearance="warning" onClick={onOpenAccess} isLoading={isLoading}>
							<FormattedMessage {...i18n.openAccessButton} />
						</Button>
					</Inline>
				</Inline>
			</ModalFooter>
		</Modal>
	);
};
